import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MaruyaApiError } from '../../lib/maruya-api'
import {
  displayDocumentTitle,
  extractReferenceUrl,
  sourceTypeLabel,
  sourceUriLabel,
} from '../../lib/document-display'
import { getDocument } from '../../lib/rag-api'
import { usePermissions } from '../../auth/usePermissions'
import type { RagDocumentDetail } from '../../types/api'
import { IconChevronLeft, IconTrash } from '../../components/ui/Icon'
import { usePageHeader } from '../../components/layout/PageHeaderContext'
import { DeleteRequestDialog } from '../../components/rag/DeleteRequestDialog'

export function DocumentDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { canCurate, canApprove } = usePermissions()
  const [document, setDocument] = useState<RagDocumentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  useEffect(() => {
    if (!id) {
      return
    }
    setLoading(true)
    getDocument(id)
      .then(setDocument)
      .catch((err) => {
        setError(
          err instanceof MaruyaApiError
            ? (err.body.message ?? err.message)
            : '詳細の取得に失敗しました',
        )
      })
      .finally(() => setLoading(false))
  }, [id])

  const heading = useMemo(() => {
    if (!document) {
      return 'ナレッジ詳細'
    }
    return displayDocumentTitle(
      document.title,
      document.source_uri,
      document.document_id,
    )
  }, [document])

  const officeUrl = useMemo(
    () => (document ? extractReferenceUrl(document.body) : null),
    [document],
  )

  usePageHeader('B. ナレッジ参照', heading)

  return (
    <div className="page page-wide">
      <button
        type="button"
        className="back-link"
        onClick={() => navigate(canApprove ? '/rag/documents' : '/rag/chat')}
      >
        <IconChevronLeft />
        {canApprove ? '承認待ちに戻る' : 'AI 質問に戻る'}
      </button>

      {loading && <p className="muted">読み込み中…</p>}
      {error && <p className="form-error">{error}</p>}

      {document && (
        <section className="card document-detail">
          <dl className="meta-list status-grid">
            <div>
              <dt>出典種別</dt>
              <dd>{sourceTypeLabel(document.source_uri) || document.source_type}</dd>
            </div>
            <div>
              <dt>更新日時</dt>
              <dd>{document.updated_at || '—'}</dd>
            </div>
            <div>
              <dt>出典</dt>
              <dd>
                <span title={document.source_uri}>
                  {sourceUriLabel(document.source_uri)}
                </span>
              </dd>
            </div>
          </dl>

          {officeUrl && (
            <p className="actions-row">
              <a
                href={officeUrl}
                className="btn btn-ghost"
                target="_blank"
                rel="noreferrer"
              >
                Office で開く
              </a>
            </p>
          )}

          <div className="document-body">
            <h2>本文</h2>
            <pre>{document.body}</pre>
          </div>

          {canCurate && (
            <p className="actions-row">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteOpen(true)}
              >
                <IconTrash />
                このナレッジを削除申請
              </button>
            </p>
          )}
        </section>
      )}

      {document && deleteOpen && (
        <DeleteRequestDialog
          document={{
            document_id: document.document_id,
            title: heading,
          }}
          onClose={() => setDeleteOpen(false)}
          onSuccess={() => setDeleteOpen(false)}
        />
      )}
    </div>
  )
}
